'use client';

import { useEffect, useRef } from 'react';

const ownerKey = '__owlscranModal';
const pendingPops = new Map<string, number>();
let traversal: { waiting: Set<() => void> } | null = null;

function currentOwner(): string | undefined {
  return history.state?.[ownerKey];
}

/**
 * Pushes a history entry when a modal opens so that iOS swipe-back
 * (and the browser back button) closes the modal instead of navigating away.
 */
export function useModalHistory(opened: boolean, close: () => void) {
  const closeRef = useRef(close);
  const ownerRef = useRef<string | null>(null);

  useEffect(() => {
    closeRef.current = close;
  }, [close]);

  useEffect(() => {
    if (!opened) return;
    const owner = (ownerRef.current ??= crypto.randomUUID());
    const openedUrl = window.location.href;
    let listening = false;
    let disposed = false;
    let waitingFor: typeof traversal = null;

    const onPopState = () => {
      // Returning from a nested modal leaves this modal's entry in place.
      if (currentOwner() !== owner) closeRef.current();
    };

    const start = () => {
      if (disposed || window.location.href !== openedUrl) return;
      const previousOwner = currentOwner();
      const pending = previousOwner ? pendingPops.get(previousOwner) : undefined;
      if (previousOwner && pending !== undefined) {
        // StrictMode remounts and replacement modal instances can both reuse
        // the departing modal's entry before its deferred traversal starts.
        window.clearTimeout(pending);
        pendingPops.delete(previousOwner);
        history.replaceState({ ...history.state, [ownerKey]: owner }, '');
      } else {
        history.pushState({ ...history.state, [ownerKey]: owner }, '');
      }
      window.addEventListener('popstate', onPopState);
      listening = true;
    };

    if (traversal) {
      // A browser traversal cannot be cancelled after back() is called.
      // Wait to push and listen until it finishes, otherwise its popstate
      // would consume and close the newly mounted modal.
      waitingFor = traversal;
      traversal.waiting.add(start);
    } else {
      start();
    }

    return () => {
      disposed = true;
      waitingFor?.waiting.delete(start);
      if (!listening) return;
      window.removeEventListener('popstate', onPopState);
      if (currentOwner() !== owner) return;

      const timer = window.setTimeout(() => {
        pendingPops.delete(owner);
        // Never consume a different modal or a route pushed after cleanup.
        if (currentOwner() !== owner || window.location.href !== openedUrl) return;
        const pendingTraversal = { waiting: new Set<() => void>() };
        traversal = pendingTraversal;
        window.addEventListener(
          'popstate',
          () => {
            // Finish dispatch before attaching listeners for waiting modals.
            queueMicrotask(() => {
              if (traversal === pendingTraversal) traversal = null;
              pendingTraversal.waiting.forEach((resume) => resume());
            });
          },
          { once: true },
        );
        history.back();
      }, 0);
      pendingPops.set(owner, timer);
    };
  }, [opened]);
}
